import Swiper from "swiper/bundle";

import "swiper/css";
import "swiper/css/bundle";

const isYouTubeUrl = (url = "") => /(?:youtu\.be\/|youtube\.com\/)/i.test(url);
const isVimeoUrl = (url = "") => /vimeo\.com\//i.test(url);

const getHeroEmbedSrc = (url = "") => {
	if (!url) return "";

	try {
		const parsed = new URL(url, window.location.origin);
		if (isYouTubeUrl(url)) {
			let id = "";
			if (parsed.hostname.includes("youtu.be")) {
				id = parsed.pathname.replace(/^\//, "").split("/")[0] || "";
			} else {
				id = parsed.searchParams.get("v") || "";
				if (!id) {
					const parts = parsed.pathname.split("/").filter(Boolean);
					const embedIndex = parts.findIndex((p) => ["embed", "v", "shorts"].includes(p));
					if (embedIndex >= 0 && parts[embedIndex + 1]) {
						id = parts[embedIndex + 1];
					}
				}
			}

			if (!id) return "";

			const query = new URLSearchParams(parsed.search);
			if (!query.get("start") && query.get("t")) {
				query.set("start", query.get("t"));
				query.delete("t");
			}
			query.set("autoplay", "1");
			query.set("mute", "1");
			query.set("loop", "1");
			if (!query.get("playlist")) query.set("playlist", id);
			if (!query.get("playsinline")) query.set("playsinline", "1");

			return `https://www.youtube.com/embed/${encodeURIComponent(id)}?${query.toString()}`;
		}

		if (isVimeoUrl(url)) {
			const parts = parsed.pathname.split("/").filter(Boolean);
			const videoSegmentIndex = parts.indexOf("video");
			let id = "";
			let token = "";

			if (videoSegmentIndex >= 0) {
				id = parts[videoSegmentIndex + 1] || "";
			} else {
				id = parts.find((part) => /^\d+$/.test(part)) || "";
				if (id) {
					const idIndex = parts.indexOf(id);
					if (idIndex >= 0 && parts[idIndex + 1]) {
						token = parts[idIndex + 1];
					}
				}
			}
			if (!id) return "";

			const query = new URLSearchParams(parsed.search);
			if (token && !query.get("h")) query.set("h", token);
			query.set("autoplay", "1");
			query.set("muted", "1");
			query.set("loop", "1");

			return `https://player.vimeo.com/video/${encodeURIComponent(id)}?${query.toString()}`;
		}
	} catch (error) {
		return "";
	}

	return "";
};

const createHeroVideoIframe = (url = "") => {
	const src = getHeroEmbedSrc(url);
	if (!src) return null;

	const iframe = document.createElement("iframe");
	iframe.src = src;
	iframe.width = "640";
	iframe.height = "360";
	iframe.frameBorder = "0";
	iframe.allow = "autoplay; encrypted-media; fullscreen; picture-in-picture";
	iframe.allowFullscreen = true;
	iframe.loading = "lazy";

	return iframe;
};

const teardownSlideVideo = (slideEl) => {
	if (!slideEl) return;
	const container = slideEl.querySelector("[data-hero-video='1']");
	if (!container) return;
	container.innerHTML = "";
	container.dataset.videoLoaded = "0";
};

const getSlideByIndex = (swiper, index) => {
	if (!swiper || !Array.isArray(swiper.slides) || !swiper.slides.length) return null;
	const total = swiper.slides.length;
	const normalized = ((index % total) + total) % total;
	return swiper.slides[normalized] || null;
};

const syncHeroVideoPool = (swiper, activeIndex) => {
	if (!swiper || !Array.isArray(swiper.slides) || !swiper.slides.length) return;

	const total = swiper.slides.length;
	const currentIndex = typeof activeIndex === "number" ? activeIndex : swiper.activeIndex;
	const keepIndexes = new Set([
		((currentIndex % total) + total) % total,
		(((currentIndex + 1) % total) + total) % total,
	]);

	keepIndexes.forEach((index) => {
		const slide = getSlideByIndex(swiper, index);
		loadSlideVideo(slide);
	});

	swiper.slides.forEach((slide, index) => {
		if (!keepIndexes.has(index)) {
			teardownSlideVideo(slide);
		}
	});
};

const loadSlideVideo = (slideEl) => {
	if (!slideEl) return;
	const container = slideEl.querySelector("[data-hero-video='1']");
	if (!container || container.dataset.videoLoaded === "1") return;

	const videoUrl = container.dataset.videoUrl || "";
	const iframe = createHeroVideoIframe(videoUrl);
	if (!iframe) return;

	const wrapper = document.createElement("div");
	wrapper.className = "video-embed-container";
	wrapper.appendChild(iframe);
	container.appendChild(wrapper);
	container.dataset.videoLoaded = "1";
};

const initHeroSlideVideos = (swiper) => {
	if (!swiper || !Array.isArray(swiper.slides)) return;

	syncHeroVideoPool(swiper, swiper.activeIndex);

	swiper.on("slideChangeTransitionStart", () => {
		syncHeroVideoPool(swiper, swiper.activeIndex);
	});

	swiper.on("slideChangeTransitionEnd", () => {
		syncHeroVideoPool(swiper, swiper.activeIndex);
	});
};

export const initSlideshows = () => {
	const slideshowConfigs = [
		{
			selector: ".swiper:not(.event-grid-swiper)",
			options: {
				spaceBetween: 10,
			},
		},
		{
			selector: ".event-grid-swiper",
			options: {
				spaceBetween: 20,
				slidesPerView: 1,
				autoHeight: true,
				grabCursor: true,
				watchOverflow: true,
				navigation: true,
			},
		},
		{
			selector: ".hero-swiper",
			options: {
				slidesPerView: 1,
				autoHeight: false,
				rewind: true,
				watchOverflow: true,
				pagination: true,
				autoplay: {
					delay: 6000,
				},
			},
		},
	];

	const selectors = slideshowConfigs.map(({ selector }) => selector).join(", ");
	if (!document.querySelector(selectors)) return;

	const baseOptions = {
		speed: 400,
	};

	slideshowConfigs.forEach(({ selector, options }) => {
			document.querySelectorAll(selector).forEach((el) => {
			const isEventGrid = selector === ".event-grid-swiper";
			const isHero = selector === ".hero-swiper";
			const columnsMobile = parseInt(el.dataset.columnsMobile || "1", 10);
			const columnsTablet = parseInt(el.dataset.columnsTablet || "2", 10);
			const columnsDesktop = parseInt(el.dataset.columnsDesktop || "3", 10);
			const twoRowDesktop = el.dataset.twoRowDesktop === "1";

			const resolvedOptions = {
				...baseOptions,
				...options,
			};

			if (isEventGrid) {
				resolvedOptions.autoHeight = !twoRowDesktop;
				resolvedOptions.rewind = true;
				resolvedOptions.breakpoints = {
					500: {
						slidesPerView: columnsMobile,
						slidesPerGroup: columnsMobile,
						spaceBetween: 20,
					},
					768: {
						slidesPerView: columnsTablet,
						slidesPerGroup: columnsTablet,
						spaceBetween: 24,
					},
					1024: {
						slidesPerView: columnsDesktop,
						slidesPerGroup: columnsDesktop,
						spaceBetween: 24,
						...(twoRowDesktop ? { grid: { rows: 2, fill: "row" } } : {}),
					},
				};

				if (twoRowDesktop) {
					resolvedOptions.watchOverflow = false;
				}
			}

			if (options.navigation === true) {
				const carouselContainer =
					el.closest(".event-card-carousel") || el.closest("section") || el.parentElement;
				const nextEl =
					carouselContainer?.querySelector("[data-swiper-next]") ||
					carouselContainer?.querySelector(".related-events-swiper-next");
				const prevEl =
					carouselContainer?.querySelector("[data-swiper-prev]") ||
					carouselContainer?.querySelector(".related-events-swiper-prev");

				resolvedOptions.navigation = {
					nextEl,
					prevEl,
				};
			}

			if (isHero && options.pagination === true) {
				const paginationEl = el.querySelector(".hero-slider-pagination");

				if (paginationEl) {
					resolvedOptions.pagination = {
						el: paginationEl,
						clickable: true,
					};
				}
			}

			const swiper = new Swiper(el, resolvedOptions);

			if (isHero) {
				initHeroSlideVideos(swiper);
			}
		});
	});
};
